/**
 * AuthService — authentication API wrapper.
 *
 * Talks to the DoCity backend at https://api.docity.co.
 * Every method returns either { data: ... } on success or a descriptive
 * string / { message } on failure, matching the response shapes the
 * existing components already check for.
 */

const BASE_URL = "https://api.docity.co";

/** Shared fetch helper — keeps token handling and error normalisation in one place. */
async function request(endpoint, options = {}) {
  const token = localStorage.getItem("token");
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  try {
    const res = await fetch(`${BASE_URL}${endpoint}`, {
      ...options,
      headers,
    });

    const contentType = res.headers.get("content-type");
    let json = null;
    if (contentType && contentType.includes("application/json")) {
      json = await res.json();
    }

    if (!res.ok) {
      const msg = json?.message || (res.status === 404 ? "Endpoint not found." : `Request failed (${res.status})`);
      return `Error: ${msg}`;
    }
    return { data: json };
  } catch (err) {
    console.error(`AuthService ${endpoint}:`, err);
    return `Error: ${err.message && !err.message.includes('Unexpected token') ? err.message : "Request failed. Please try again."}`;
  }
}

const AuthService = {
  /** Register a new user. */
  Signup(data) {
    return request("/api/Signup", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Log in with email + password. */
  Login(data) {
    return request("/api/Login", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Verify a 6-digit OTP. */
  verifyOtp(data) {
    return request("/api/verifyOtp", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Re-send OTP to the given email. */
  resendOtp(data) {
    return request("/api/resendOtp", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Request a password-reset OTP. */
  forgotPassword(data) {
    return request("/api/forgotPassword", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Change password using OTP verification. */
  changePassword(data) {
    return request("/api/changePassword", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Google OAuth login/register. */
  googleLogin(data) {
    return request("/api/google-auth", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Edit user profile data. */
  editDoCityUserData(data) {
    return request("/editDoCityUserData", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },

  /** Update user password (from profile / settings). */
  UpdatePassword(data) {
    return request("/api/UpdatePassword", {
      method: "POST",
      body: JSON.stringify(data),
    });
  },
};

export default AuthService;
